<script lang="ts">

    import { Component, Emit, mixins } from "vue-facing-decorator";
    import { AccessibilityHandler, IconName, PerformanceQASelectors, TranslationKey } from "webshop-modules";
    import { Selection, TicketSelectionDisplayModeState } from "@/domain/ticket-selection";
    import IconComponent from "@/components/atoms/Icon.vue";
    import { BreakpointHandler, TicketSelectionDisplay } from "@/mixins";

    @Component({
    components: {IconComponent}
})
export default class ModeSelectionComponent extends mixins(TicketSelectionDisplay, BreakpointHandler) {

    public readonly IconName: typeof IconName = IconName;
    public readonly Translation: typeof TranslationKey = TranslationKey;
    public readonly PerformanceQASelectors: typeof PerformanceQASelectors = PerformanceQASelectors;
    public readonly Selection: typeof Selection = Selection;

    @Emit()
    public activateSeatmap(): TicketSelectionDisplayModeState {
        return {
            bestSeat: false,
            seatmap: {
                isSeatmapVisible: true,
                isMenuExpanded: false,
            }
        };
    }

    @Emit()
    public activateBestseat(): TicketSelectionDisplayModeState {
        return {
            bestSeat: true,
            seatmap: {
                isSeatmapVisible: false,
                isMenuExpanded: false,
            }
        };
    }

    @Emit()
    public toggleEventHeaderExpansion(): TicketSelectionDisplayModeState {
        return {
            bestSeat: false,
            seatmap: {
                isSeatmapVisible: true,
                isMenuExpanded: !this.displayModeState.seatmap.isMenuExpanded,
            }
        };
    }

    public get bestseatIconName(): IconName {
        return this.displayModeState.bestSeat ? IconName.BestSeatActive : IconName.BestSeat;
    }

    public get seatmapIconName(): IconName {
        return this.displayModeState.seatmap.isSeatmapVisible ? IconName.SeatingChartActive : IconName.SeatingChart;
    }

    public handleKeyPress(event: KeyboardEvent, selection: Selection) {
        AccessibilityHandler.handleActionWithCallback(event, () => {
            if (selection === Selection.SEATMAP) {
                this.activateSeatmap();
            }

            if (selection === Selection.BEST_SEAT) {
                this.activateBestseat();
            }
        });
    }

    private isMobileMenuOpen() {
        return this.isMobile && this.displayStateStore.modeSelectionState.seatmap.isMenuExpanded;
    }
}
</script>

<template>
    <div id="mode-selection">
        <template v-if="isMobile">
            <div v-on:click="activateSeatmap()"
                 v-if="displayModeState.bestSeat"
                 class="a-ticketModeOption"
                 tabindex="0"
                 @keydown="handleKeyPress($event, Selection.SEATMAP)">
                <icon-component class="a-ticketModeOption__icon"
                                :icon-id="IconName.BestSeat"/>

                <div class="a-ticketModeOption__content">
                    <h2 v-translate="{ key: Translation.Seatmap_trigger }">Seatmap selection</h2>
                    <p v-translate="{ key: Translation.Seatmap_trigger_detail }">Choose your own seats</p>
                </div>

                <div class="a-ticketModeOption__indicator -is-modal-link">
                    <div class="a-ticketModeOption__indicatorCircle" :data-qa="PerformanceQASelectors.PRICE_LEVEL_CIRCLE_INDICATOR">
                        <icon-component :icon-id="IconName.ArrowFull"/>
                    </div>
                </div>
            </div>

            <div v-if="isMobileMenuOpen()"
                 v-on:click="activateBestseat()"
                 class="a-ticketModeOption is-seatmap-active"
                 tabindex="0"
                 @keydown="handleKeyPress($event, Selection.BEST_SEAT)">
                <icon-component class="a-ticketModeOption__icon"
                                :icon-id="IconName.Arrow"/>
                <div>
                    <p v-translate="{ key: Translation.Seatmap_exit_seatmap_selection }">Back to best seat
                        selection</p>
                    <hr>
                </div>
            </div>

            <div v-if="isMobileMenuOpen()"
                 class="a-ticketModeOption vertical-filler">
            </div>

            <div v-if="isMobile && displayModeState.seatmap.isSeatmapVisible"
                 v-on:click="toggleEventHeaderExpansion()"
                 class="a-ticketModeOption__expand">
                <IconComponent class="a-ticketModeOption__arrowDown" :icon-id="IconName.Arrow"
                               v-bind:class="{ '-rotatedLeft': isMobileMenuOpen(), '-rotatedRight': !isMobileMenuOpen() }"/>
            </div>
        </template>

        <template v-else>
            <div class="a-ticketModeSelection">
                <div v-on:click="activateBestseat()"
                     class="a-ticketModeOption"
                     tabindex="0"
                     @keydown="handleKeyPress($event, Selection.BEST_SEAT)">
                    <icon-component class="a-ticketModeOption__icon"
                                    :icon-id="bestseatIconName"/>

                    <div class="a-ticketModeOption__content">
                        <h2 v-translate="{ key: Translation.Bestseat_trigger }">Best seat</h2>
                        <p v-translate="{ key: Translation.Bestseat_trigger_detail }">Choose your price, we choose your
                            seats</p>
                    </div>

                    <div class="a-ticketModeOption__indicator"
                         :class="{ '-is-active': displayModeState.bestSeat }">
                        <div class="a-ticketModeOption__indicatorCircle" :data-qa="PerformanceQASelectors.PRICE_LEVEL_CIRCLE_INDICATOR"></div>
                    </div>
                </div>

                <div v-on:click="activateSeatmap"
                     class="a-ticketModeOption"
                     tabindex="0"
                     @keydown="handleKeyPress($event, Selection.SEATMAP)">
                    <icon-component class="a-ticketModeOption__icon"
                                    :icon-id="seatmapIconName"/>

                    <div class="a-ticketModeOption__content">
                        <h2 v-translate="{ key: Translation.Seatmap_trigger }">Seatmap selection</h2>
                        <p v-translate="{ key: Translation.Seatmap_trigger_detail }">Choose your own seats</p>
                    </div>

                    <div class="a-ticketModeOption__indicator"
                         :class="{ '-is-active': displayModeState.seatmap.isSeatmapVisible }">
                        <div class="a-ticketModeOption__indicatorCircle" :data-qa="PerformanceQASelectors.PRICE_LEVEL_CIRCLE_INDICATOR"></div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>
